import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Copy,
  Download,
  FilePlus2,
  FolderOpen,
  HardDrive,
  Import,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { ProjectRecord, StoryboardProject } from '../types';
import { BrandMark } from './Brand';
import { AppHeader } from './AppHeader';
import {
  deleteProject,
  listProjectRecords,
  requestPersistentStorage,
  saveProject,
  storageEstimate,
} from '../storage/db';
import { cloneProjectWithNewId } from '../model/factories';
import { buildUnexpectedIdeaProject, createTemplateById, templates } from '../model/templates';
import { CellSvg } from '../render/CellSvg';
import { exportProjectPackage, importProjectFile } from '../utils/export';
import { goToEditor } from '../utils/navigation';

const formatDate = (date: string): string => {
  const value = new Date(date);
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(value);
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
};

export const Dashboard: React.FC = () => {
  const [records, setRecords] = useState<ProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [persistent, setPersistent] = useState<'granted' | 'denied' | 'unsupported' | 'checking'>('checking');
  const [busy, setBusy] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const templatePreviews = useMemo(() => templates.map((template) => ({ template, project: template.build() })), []);

  const refresh = useCallback(async () => {
    setLoading(true);
    const items = await listProjectRecords();
    if (items.length === 0 && localStorage.getItem('storyboardsmith.seeded') !== 'yes') {
      const starter = buildUnexpectedIdeaProject();
      await saveProject(starter);
      localStorage.setItem('storyboardsmith.seeded', 'yes');
      setRecords(await listProjectRecords());
    } else {
      setRecords(items);
    }
    setLoading(false);
    setStorage(await storageEstimate());
  }, []);

  useEffect(() => {
    void refresh();
    void requestPersistentStorage().then(setPersistent);
  }, [refresh]);

  const createProject = async (templateId: string) => {
    setBusy(templateId);
    try {
      const project = createTemplateById(templateId);
      await saveProject(project);
      goToEditor(project.id);
    } finally {
      setBusy(null);
    }
  };

  const duplicate = async (project: StoryboardProject) => {
    const copy = cloneProjectWithNewId(project);
    await saveProject(copy);
    await refresh();
  };

  const remove = async (project: StoryboardProject) => {
    if (!window.confirm(`Delete “${project.title}” from this browser? This cannot be undone.`)) return;
    await deleteProject(project.id);
    await refresh();
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy('import');
    try {
      const project = await importProjectFile(file);
      await saveProject(project);
      goToEditor(project.id);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'The project could not be imported.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="dashboard-shell">
      <AppHeader
        onHome={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        leading={<strong className="ss-dashboard-title">Your storyboards</strong>}
        fileActions={[
          { label: 'New comic', icon: <FilePlus2 size={16} />, disabled: !!busy, onSelect: () => void createProject('blank') },
          { label: 'Open comic…', icon: <Import size={16} />, disabled: !!busy, onSelect: () => importRef.current?.click() },
        ]}
        controls={<>
          <button type="button" className="secondary-button" onClick={() => importRef.current?.click()} disabled={!!busy}><Import size={16} />{busy === 'import' ? 'Importing…' : 'Import project'}</button>
          <button type="button" className="primary-button" onClick={() => void createProject('blank')} disabled={!!busy}><Plus size={16} /> New storyboard</button>
        </>}
      />
      <input ref={importRef} hidden type="file" aria-label="Import project file" accept=".smithboard,.json,application/zip,application/json" onChange={handleImport} />

      <main>
        <section className="hero-section">
          <div className="hero-copy">
            <div className="eyebrow"><Sparkles size={15} /> Browser-only visual storytelling</div>
            <h1>Shape ideas into scenes people understand.</h1>
            <p>StoryboardSmith is a private, local-first storyboard and comic creator. Your projects stay on this device, work offline after installation, and export without a server.</p>
            <div className="hero-actions">
              <button type="button" className="primary-button primary-large" onClick={() => void createProject('unexpected')}>
                Open the sample story <ArrowRight size={20} />
              </button>
              <button type="button" className="secondary-button primary-large" onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}>
                Browse templates
              </button>
            </div>
            <div className="privacy-row">
              <span><ShieldCheck size={17} /> No account required</span>
              <span><HardDrive size={17} /> Saved in your browser</span>
              <span><Download size={17} /> Portable project files</span>
            </div>
          </div>
          <div className="hero-preview" aria-label="StoryboardSmith editor preview">
            <div className="preview-toolbar"><span /><span /><span /><strong>StoryboardSmith</strong></div>
            <div className="preview-workspace">
              <aside className="preview-assets"><div /><div /><div /><div /></aside>
              <div className="preview-canvas">
                <CellSvg project={templatePreviews[0].project} cell={templatePreviews[0].project.cells[0]} preserveAspectRatio="xMidYMid meet" />
              </div>
              <aside className="preview-inspector"><div /><div /><div /><div /><div /></aside>
            </div>
          </div>
        </section>

        <section className="dashboard-section" aria-labelledby="projects-heading">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Your browser library</span>
              <h2 id="projects-heading">Recent storyboards</h2>
            </div>
            <div className="storage-pill" title="Estimated browser storage used by this origin">
              <HardDrive size={16} />
              {storage ? `${formatBytes(storage.usage)} used of ${formatBytes(storage.quota)}` : 'Storage estimate unavailable'}
              <span className={`storage-dot ${persistent === 'granted' ? 'is-good' : ''}`} />
              {persistent === 'granted' ? 'Persistent' : persistent === 'checking' ? 'Checking…' : 'Browser managed'}
            </div>
          </div>

          {loading ? (
            <div className="empty-state"><div className="spinner" /> Loading your local projects…</div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <FolderOpen size={40} />
              <h3>No storyboards yet</h3>
              <p>Start from a template or import a portable .smithboard file.</p>
            </div>
          ) : (
            <div className="project-grid">
              {records.map((record) => {
                const project = record.document;
                return (
                  <article className="project-card" key={record.id}>
                    <button type="button" className="project-preview" onClick={() => goToEditor(record.id)} aria-label={`Open ${record.title}`}>
                      <CellSvg project={project} cell={project.cells[0]} preserveAspectRatio="xMidYMid slice" />
                      <span className="project-cell-count">{record.cellCount} {record.cellCount === 1 ? 'scene' : 'scenes'}</span>
                    </button>
                    <div className="project-card-body">
                      <button type="button" className="project-title-link" onClick={() => goToEditor(record.id)}>{record.title}</button>
                      <p>Edited {formatDate(record.updatedAt)}</p>
                      <div className="project-actions">
                        <button type="button" className="small-button" onClick={() => goToEditor(record.id)}><FolderOpen size={16} /> Open</button>
                        <button type="button" className="icon-button" onClick={() => void duplicate(project)} title="Duplicate"><Copy size={17} /></button>
                        <button type="button" className="icon-button" onClick={() => void exportProjectPackage(project)} title="Download project"><Download size={17} /></button>
                        <button type="button" className="icon-button danger" onClick={() => void remove(project)} title="Delete"><Trash2 size={17} /></button>
                        <button type="button" className="icon-button" disabled title="More actions"><MoreHorizontal size={17} /></button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="dashboard-section templates-section" id="templates" aria-labelledby="templates-heading">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Original starter library</span>
              <h2 id="templates-heading">Start with a useful structure</h2>
              <p>Every template is editable, stored locally, and built from StoryboardSmith’s original vector artwork.</p>
            </div>
          </div>
          <div className="template-grid">
            {templatePreviews.map(({ template, project }) => (
              <button
                key={template.id}
                className="template-card"
                type="button"
                onClick={() => void createProject(template.id)}
                disabled={busy === template.id}
                style={{ '--template-accent': template.accent } as React.CSSProperties}
              >
                <div className="template-preview">
                  <CellSvg project={project} cell={project.cells[0]} preserveAspectRatio="xMidYMid slice" />
                  <span className="template-plus"><FilePlus2 size={20} /></span>
                </div>
                <span className="template-name">{template.name}</span>
                <span className="template-description">{template.description}</span>
                <span className="template-use">{busy === template.id ? 'Creating…' : 'Use template'} <ArrowRight size={16} /></span>
              </button>
            ))}
          </div>
        </section>
      </main>

      <footer className="dashboard-footer">
        <BrandMark />
        <p>Private by design. No backend, tracking pixel, account, or cloud database is required.</p>
      </footer>
    </div>
  );
};
